import { Dialog } from '@angular/cdk/dialog';
import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { DialogData } from '@app/components/alert-modal/alert-modal.component';
import { SpacesStore } from '@app/store/spaces.store';
import { SpaceUserRole } from '@interfaces/space-access-control.types';
import { User } from '@interfaces/user';
import { TranslateService } from '@ngx-translate/core';
import { DeviceTypeService } from '@services/device-type.service';
import { NavigationService } from '@services/navigation.service';
import { SpaceRolePermissionService } from '@services/space-role.service';
import { UserService } from '@services/user.service';
import { UtilsService } from '@services/utils.service';
import { getTranslation } from '@shared/translate-helper';
import { NgxSpinnerService } from 'ngx-spinner';
import { Space, SpaceTag } from './../../interfaces/space';

interface AddSpaceForm {
  name: string;
  company: { uuid: string };
  tags?: SpaceTag[];
}

enum DialogType {
  ADD_SPACE = 'ADD_SPACE',
  UPDATE_SPACE = 'UPDATE_SPACE',
  DELETE_PROMPT = 'DELETE_PROMPT',
  ARCHIVE_PROMPT = 'ARCHIVE_PROMPT',
  UNARCHIVE_PROMPT = 'UNARCHIVE_PROMPT',
  CLONE_PROMPT = 'CLONE_PROMPT',
}

const DIALOG_CONFIG = {
  [DialogType.ADD_SPACE]: {
    panelClass: ['med_add-space-dialog', 'med_modal-dialog'],
  },
  [DialogType.UPDATE_SPACE]: {
    panelClass: ['md_dialog_wrapper', 'med_modal-dialog'],
  },
  [DialogType.DELETE_PROMPT]: {
    panelClass: ['md_dialog_wrapper', 'med_modal-dialog'],
  },
  [DialogType.ARCHIVE_PROMPT]: {
    panelClass: ['md_dialog_wrapper', 'med_modal-dialog'],
  },
  [DialogType.UNARCHIVE_PROMPT]: {
    panelClass: ['md_dialog_wrapper', 'med_modal-dialog'],
  },
  [DialogType.CLONE_PROMPT]: {
    panelClass: ['md_dialog_wrapper', 'med_modal-dialog'],
  },
};

const empty_space: Space = { id: '', uuid: '', name: '', record_count: 0 };

@Component({
  selector: 'app-my-spaces',
  templateUrl: './spaces.component.html',
  styleUrls: ['./spaces.component.scss'],
  standalone: false,
})
export class SpacesComponent implements OnInit, OnDestroy {
  @ViewChild('addSpaceDialog') addSpaceDialog!: TemplateRef<any>;
  @ViewChild('updateSpaceDialog') updateSpaceDialog!: TemplateRef<any>;
  @ViewChild('deletePrompt') deletePrompt!: TemplateRef<any>;
  @ViewChild('archivePrompt') archivePrompt!: TemplateRef<any>;
  @ViewChild('unarchivePrompt') unarchivePrompt!: TemplateRef<any>;
  @ViewChild('clonePrompt') clonePrompt!: TemplateRef<any>;
  @ViewChild('filterTagAutofocus', { read: ElementRef }) filterInput?: ElementRef<HTMLInputElement>;
  public readonly spacesStore: InstanceType<typeof SpacesStore> = inject(SpacesStore);

  public loading = false;
  public showingArchivedSpaces = false;
  public currentPage = 0;
  public total = 0;
  public user!: User;
  public activeTableHeadSort = 'name';
  public createFilterTagMenu = '';
  public showErrors = false;
  public addSpaceForm: AddSpaceForm = { name: '', company: { uuid: '' } };
  public selectedSpace: Space = empty_space;
  public spaceFilter = '';
  public isLoading: boolean = false;
  public isSomeTagMenuOpen: boolean = false;

  // Signal para el filtro de tags
  public createFilterTagMenuSignal = signal<string>('');

  // Computed que se actualiza automáticamente cuando userTags() o el filtro cambian
  public filteredTags = computed(() => {
    const filterValue = this.createFilterTagMenuSignal().toLowerCase().trim();
    const userTags = this.spacesStore.userTags();

    if (!filterValue) return userTags;
    return userTags.filter((tag) => tag.name.toLowerCase().includes(filterValue));
  });

  private originalName = '';
  public usersRoles: { value: string; label: string }[] = [];
  public sortOptions: { value: string; label: string }[] = [];

  public createSpaceTagList: SpaceTag[] = [];

  public tagColorList: { value: string; label: string; class: string; colorCode: string }[] = [
    { value: 'pale-peach', label: 'Pale Peach', class: 'pale-peach', colorCode: '#FAD6D6' },
    { value: 'pale-blue', label: 'Pale Blue', class: 'pale-blue', colorCode: '#D6EAF8' },
    { value: 'light-pink', label: 'Light Pink', class: 'light-pink', colorCode: '#FAD6E6' },
    { value: 'pale-cyan', label: 'Pale Cyan', class: 'pale-cyan', colorCode: '#D6F8F8' },
    { value: 'buttercream', label: 'Buttercream', class: 'buttercream', colorCode: '#F9E6B2' },
    { value: 'mint-green', label: 'Mint Green', class: 'mint-green', colorCode: '#B2E6B2' },
    { value: 'tea-green', label: 'Tea Green', class: 'tea-green', colorCode: '#B2E6D6' },
    { value: 'lavender', label: 'Lavender', class: 'lavender', colorCode: '#E6B2E6' },
  ];

  public errorText: string = '';
  public maxTagInputLength: number = 45;

  public workspaces = computed(() => {
    const userData = this.userService.getUserData();
    return (
      userData?.companies?.map((company) => ({
        label:
          company.uuid === userData?.company?.uuid
            ? company.name + ` (${this.translateService.instant('current')})`
            : company.name,
        ...company,
      })) || []
    );
  });
  public selectedWorkspace: { uuid: string; name: string } | null = null;
  public cloneSpaceName: string = '';

  constructor(
    public utilsService: UtilsService,
    private userService: UserService,
    public translateService: TranslateService,
    public deviceType: DeviceTypeService,
    private dialog: Dialog,
    private spinner: NgxSpinnerService,
    private navigationService: NavigationService,
    private spaceRolePermissionService: SpaceRolePermissionService,
  ) {
    effect(() => {
      // Set selected workspace based on user's current company
      const userData = this.userService.getUserData();
      if (userData) {
        this.selectedWorkspace =
          this.workspaces().find((workspace) => workspace.uuid === userData?.company?.uuid) || null;
      }
    });
  }

  ngOnInit(): void {
    this.translateService
      .get(['all', 'viewer', 'space_user', 'owner', 'contributor', 'name', 'last_update'])
      .subscribe((t) => {
        this.usersRoles = [
          { value: 'all', label: t['all'] },
          { value: 'viewer', label: t['viewer'] },
          { value: 'user', label: t['space_user'] },
          { value: 'owner', label: t['owner'] },
          { value: 'contributor', label: t['contributor'] },
        ];

        this.sortOptions = [
          { value: 'name', label: t['name'] },
          { value: 'last_update', label: t['last_update'] },
        ];
      });

    const isStale =
      !this.spacesStore.lastUpdated() ||
      Date.now() - (this.spacesStore.lastUpdated() ?? 0) > this.spacesStore.CACHE_DURATION_MS();

    if (this.spacesStore.isInitialLoad() || isStale) {
      if (this.spacesStore.isInitialLoad()) {
        this.spinner.show();
      }
      this.spacesStore.loadSpaces();
    }
  }

  private openDialog(dialogType: DialogType): void {
    const template = this.getDialogTemplate(dialogType);
    const config = DIALOG_CONFIG[dialogType];

    if (template) {
      this.dialog.open(template, { panelClass: config.panelClass, hasBackdrop: true, disableClose: true });
    }
  }

  private getDialogTemplate(dialogType: DialogType): TemplateRef<any> | null {
    switch (dialogType) {
      case DialogType.ADD_SPACE:
        return this.addSpaceDialog;
      case DialogType.UPDATE_SPACE:
        return this.updateSpaceDialog;
      case DialogType.DELETE_PROMPT:
        return this.deletePrompt;
      case DialogType.ARCHIVE_PROMPT:
        return this.archivePrompt;
      case DialogType.UNARCHIVE_PROMPT:
        return this.unarchivePrompt;
      case DialogType.CLONE_PROMPT:
        return this.clonePrompt;
      default:
        return null;
    }
  }

  private showSpaceActionFeedbackAlert(
    space: Space,
    action: 'delete' | 'edit' | 'archive' | 'clone' | 'unarchive',
    result: 'success' | 'error',
  ): void {
    let alertType: DialogData['alertType'] = 'success';
    let alertDescription = '';
    let alertTitle = '';
    switch (action) {
      case 'delete':
        alertType = result === 'success' ? 'success' : 'error';
        alertTitle =
          result === 'success'
            ? this.translateService.instant('space_deleted_successfully')
            : this.translateService.instant('space_delete_error');
        alertDescription =
          result === 'success'
            ? this.translateService.instant('space_deleted_successfully_description')
            : this.translateService.instant('space_delete_error_description');
        break;
      case 'edit':
        alertTitle =
          result === 'success'
            ? this.translateService.instant('space_updated_successfully')
            : this.translateService.instant('space_update_error');
        alertType = result === 'success' ? 'success' : 'error';
        alertDescription =
          result === 'success'
            ? this.translateService.instant('space_updated_successfully_description')
            : this.translateService.instant('space_update_error_description');
        break;
      case 'archive':
        alertTitle =
          result === 'success'
            ? this.translateService.instant('space_archived_successfully', { spaceName: space.name })
            : this.translateService.instant('space_archived_error');
        alertType = result === 'success' ? 'success' : 'error';
        alertDescription =
          result === 'success'
            ? this.translateService.instant('space_archived_successfully_description', { spaceName: space.name })
            : this.translateService.instant('space_archived_error_description');
        break;
      case 'unarchive':
        alertType = result === 'success' ? 'success' : 'error';
        alertTitle =
          result === 'success'
            ? this.translateService.instant('space_unarchived_successfully', { spaceName: space.name })
            : this.translateService.instant('space_unarchived_error');

        alertDescription =
          result === 'success'
            ? this.translateService.instant('space_unarchived_successfully_description', { spaceName: space.name })
            : this.translateService.instant('space_unarchived_error_description');
        break;
    }
    this.utilsService.showAlert({
      title: alertTitle,
      alertDescription: alertDescription,
      alertType: alertType,
      icon: alertType === 'success' ? 'circle-check-outline' : 'circle-xmark',
      showActionBtn: false,
      timed: true,
      time: 3000,
    });
  }

  openAddSpaceDialog(): void {
    this.addSpaceForm = { name: '', company: { uuid: this.userService.getUserData()?.company?.uuid || '' } };
    this.showErrors = false;
    this.openDialog(DialogType.ADD_SPACE);
  }

  openDeletePrompt(space: Space): void {
    this.selectedSpace = space;
    this.originalName = space.name;
    this.openDialog(DialogType.DELETE_PROMPT);
  }

  openUpdatePrompt(space: Space): void {
    this.selectedSpace = { ...space };
    this.originalName = space.name;
    this.openDialog(DialogType.UPDATE_SPACE);
  }

  openArchivePrompt(space: Space): void {
    this.selectedSpace = { ...space };
    this.openDialog(space.is_archived ? DialogType.UNARCHIVE_PROMPT : DialogType.ARCHIVE_PROMPT);
  }

  openUnarchivePrompt(space: Space): void {
    this.selectedSpace = { ...space };
    this.openDialog(DialogType.UNARCHIVE_PROMPT);
  }

  openClonePrompt(space: Space): void {
    this.selectedSpace = { ...space };
    this.openDialog(DialogType.CLONE_PROMPT);
    this.cloneSpaceName = space.name + ' (clone)';
    this.showErrors = false;
  }

  toggleShowArchivedSpaces(): void {
    this.spacesStore.toggleShowArchivedSpaces();
  }

  showArchivedSpaces(): void {
    if (!this.showingArchivedSpaces) this.spacesStore.toggleShowArchivedSpaces();
    this.showingArchivedSpaces = true;
  }
  showActiveSpaces(): void {
    if (this.showingArchivedSpaces) this.spacesStore.toggleShowArchivedSpaces();
    this.showingArchivedSpaces = false;
  }

  closeDialog(): void {
    if (this.selectedSpace != empty_space) {
      this.selectedSpace.name = this.originalName;
    }
    this.dialog.closeAll();
    this.selectedSpace = empty_space;
  }

  addSpace(): void {
    if (this.loading || !this.isValidForm()) return;

    this.loading = true;
    this.addSpaceForm.company.uuid = this.userService.getUserData()?.company?.uuid || '';

    if (this.createSpaceTagList.length > 0) {
      const tagData = this.createSpaceTagList.map((tag) =>
        tag.uuid ? { uuid: tag.uuid } : { name: tag.name, color: tag.color, uuid: '' },
      );
      this.addSpaceForm.tags = structuredClone(tagData) as SpaceTag[];
    }

    this.spacesStore.addSpace(this.addSpaceForm).then((res: Space | undefined) => {
      if (res != undefined) {
        res.tags = structuredClone(this.createSpaceTagList);
      }
      this.closeDialog();
      this.loading = false;
    });
  }

  private isValidForm(): boolean {
    this.showErrors = !this.addSpaceForm.name.trim();
    return !this.showErrors;
  }

  confirmDelete(): void {
    if (
      !this.selectedSpace ||
      !this.spaceRolePermissionService.checkSpaceUserPermission(
        this.selectedSpace.role as SpaceUserRole,
        'space_delete',
      )
    )
      return;

    this.spacesStore
      .deleteSpace(this.selectedSpace.uuid)
      .then(() => {
        this.closeDialog();
        this.showSpaceActionFeedbackAlert(this.selectedSpace, 'delete', 'success');
      })
      .catch(() => {
        this.closeDialog();
        this.showSpaceActionFeedbackAlert(this.selectedSpace, 'delete', 'error');
      });
  }

  updateSpace(): void {
    if (
      !this.selectedSpace ||
      this.loading ||
      !this.spaceRolePermissionService.checkSpaceUserPermission(this.selectedSpace.role as SpaceUserRole, 'space_edit')
    )
      return;

    this.loading = true;
    const spaceData = { name: this.selectedSpace.name };
    this.spacesStore
      .updateSpace(this.selectedSpace.uuid, spaceData)
      .then(() => {
        this.closeDialog();
        this.loading = false;
        this.showSpaceActionFeedbackAlert(this.selectedSpace, 'edit', 'success');
      })
      .catch(() => {
        this.loading = false;
        this.showSpaceActionFeedbackAlert(this.selectedSpace, 'edit', 'error');
      });
  }

  toggleArchiveSpace(isArchived: boolean): void {
    if (
      !this.selectedSpace ||
      this.loading ||
      !this.spaceRolePermissionService.checkSpaceUserPermission(
        this.selectedSpace.role as SpaceUserRole,
        'space_archive',
      )
    )
      return;

    this.loading = true;
    const spaceData = { is_archived: isArchived };
    this.spacesStore
      .updateSpace(this.selectedSpace.uuid, spaceData)
      .then((space) => {
        this.closeDialog();
        this.loading = false;
        this.showSpaceActionFeedbackAlert(space, isArchived ? 'archive' : 'unarchive', 'success');
      })
      .catch(() => {
        this.loading = false;
        this.showSpaceActionFeedbackAlert(this.selectedSpace, isArchived ? 'archive' : 'unarchive', 'error');
      });
  }

  gotoSpaceDetails(id: string): void {
    if (!this.isSomeTagMenuOpen) this.navigationService.navigateToSpaceDetails(id);
  }

  onNameChange(newValue: string): void {
    this.showErrors = newValue.length < 1;
  }

  cloneSpace(): void {
    if (this.loading) return;
    if (
      !this.selectedWorkspace ||
      this.loading ||
      !this.spaceRolePermissionService.checkSpaceUserPermission(
        this.selectedSpace.role as SpaceUserRole,
        'space_clone',
      ) ||
      !this.cloneSpaceName.trim()
    ) {
      return;
    }
    const cloneSpaceData = {
      space: { uuid: this.selectedSpace.uuid },
      company: { uuid: this.selectedWorkspace.uuid },
      name: this.cloneSpaceName,
    };
    this.loading = true;
    this.showErrors = false;
    this.spacesStore
      .cloneSpace(cloneSpaceData)
      .then((clonedSpace) => {
        this.loading = false;
        if (clonedSpace) {
          getTranslation(this.translateService, [
            'space_cloned_successfully_to_new_space',
            'space_cloned_successfully_to_new_space_description',
          ]).subscribe((t: any) => {
            this.closeDialog();
            this.utilsService.showAlert({
              title: t['space_cloned_successfully_to_new_space'].replace(
                '{{newSpaceName}}',
                this.selectedWorkspace?.name || '',
              ),
              alertDescription: t['space_cloned_successfully_to_new_space_description'],
              alertType: 'success',
              icon: 'circle-check-outline',
              showActionBtn: false,
              timed: true,
              time: 4000,
            });
          });
        }
      })
      .catch(() => {
        this.loading = false;
        this.showErrors = true;
      });
  }

  /**
   * Filters spaces based on the selected role.
   * @param role The role to filter spaces by.
   */
  public filterSpacesByRole(role: string): void {
    this.spacesStore.filterSpacesByRole(role);
  }

  /**
   * Sorts spaces based on the selected option.
   * @param option The option to sort spaces by ('name' or 'last_update').
   */
  public sortSpacesByOption(option: string): void {
    this.spacesStore.sortSpacesByOption(option);
  }

  public refreshSpaces(): void {
    this.spacesStore.loadSpaces(true);
  }

  ngOnDestroy(): void {
    this.spinner.hide();
  }

  public addTagToSpace(tagObg: SpaceTag, space: Space) {
    if (!space.tags) space.tags = [];
    if (
      !space.tags?.some(
        (tag) => tag.uuid == tagObg.uuid || tag.name.toLocaleLowerCase() == tagObg.name.toLocaleLowerCase(),
      )
    ) {
      space.tags?.push(tagObg);
      space.tags?.sort((a, b) => a.name.localeCompare(b.name));
      this.spacesStore.updateSpace(space.uuid, {
        tags: space.tags.map((tag) => ({ uuid: tag.uuid })) as SpaceTag[],
      });
    }
  }

  public deleteTagFromSpace(tagId: string, space: Space) {
    space.tags = space.tags?.filter((tag) => tag.uuid != tagId);
    this.spacesStore.updateSpace(space.uuid, { tags: space.tags?.map((tag) => ({ uuid: tag.uuid })) as SpaceTag[] });
  }

  public createNewUserTag(name: string) {
    const tagData = {
      name: name,
      color: this.tagColorList[Math.floor(Math.random() * this.tagColorList.length)].colorCode,
      uuid: '',
    };
    this.addTagToNewSpaceList(tagData);
    this.createFilterTagMenu = '';
  }

  public addTag(tagName: string, space: Space) {
    if (this.isLoading) return;
    this.isLoading = true;

    const tagData = {
      name: tagName,
      color: this.tagColorList[Math.floor(Math.random() * this.tagColorList.length)].colorCode.toLowerCase(),
      uuid: '',
    };

    if (!space.uuid) {
      if (!space.tags) space.tags = [];
      space.tags.push(tagData);
      space.tags.sort((a, b) => a.name.localeCompare(b.name));

      this.createSpaceTagList = [...space.tags];
      this.isLoading = false;
      return;
    }

    if (!space.tags) space.tags = [];
    space.tags.push(tagData);
    space.tags.sort((a, b) => a.name.localeCompare(b.name));

    this.spacesStore
      .updateSpace(space.uuid, {
        tags: space.tags.map((tag) => ({
          uuid: tag.uuid,
          name: tag.name,
          color: tag.color.toLowerCase(),
        })),
      })
      .then(() => {
        this.isLoading = false;
      })
      .catch(() => {
        this.isLoading = false;
      });
  }

  public updateTag(tagObg: SpaceTag) {
    if (!tagObg.uuid || !tagObg.name || !tagObg.color) return;
    this.spacesStore.updateTag({ uuid: tagObg.uuid, name: tagObg.name, color: tagObg.color.toLowerCase() }).then(() => {
      this.spacesStore.loadSpaces(true);
      this.spacesStore.tagsFilterList().forEach((tag) => {
        if (tag.uuid === tagObg.uuid) {
          tag.name = tagObg.name;
          tag.color = tagObg.color;
        }
      });
    });
  }

  public deleteTag(tagId: string, space: Space) {
    const tags = space.tags?.filter((tag) => tag.uuid !== tagId) || [];
    this.spacesStore.deleteTag(tagId).then(() => {
      this.spacesStore.updateSpace(space.uuid, { tags }).then(() => {
        space.tags = tags;
        getTranslation(this.translateService, ['tag_deleted', 'tag_deleted_description']).subscribe((t: any) => {
          this.utilsService.showAlert({
            title: t['tag_deleted'],
            alertDescription: t['tag_deleted_description'],
            alertType: 'success',
            icon: 'circle-check-outline',
            showActionBtn: false,
            timed: true,
            time: 2000,
          });
        });
      });

      this.spacesStore.tagsFilterList().splice(
        this.spacesStore.tagsFilterList().findIndex((tag) => tag.uuid === tagId),
        1,
      );
    });
  }

  public getIsTagSelected(tagId: string): boolean {
    return this.spacesStore.tagsFilterList().some((tagSelected) => tagSelected.uuid === tagId);
  }

  public toggleSelectedTagFilter(tag: SpaceTag) {
    const tagIsSelected = this.spacesStore.tagsFilterList().some((selectedTag) => selectedTag.uuid === tag.uuid);
    if (tagIsSelected) this.deleteFromSelectedTag(tag.uuid);
    else this.addToSelectedTag(tag);
  }

  private addToSelectedTag(tag: SpaceTag) {
    this.spacesStore.addToSelectedTagsFilterList(tag);
  }

  private deleteFromSelectedTag(tagId: string) {
    this.spacesStore.removeFromSelectedTagsFilterList(tagId);
  }

  public toggleSelectAll() {
    this.spacesStore.toggleSelectAllTagsInFilter();
  }

  public getShowFilteredSpaceByTag(space: Space): boolean {
    if (this.spacesStore.tagsFilterList().length > 0)
      return (
        space.tags?.some((tag) =>
          this.spacesStore.tagsFilterList().some((selectedTag) => selectedTag.uuid === tag.uuid),
        ) ?? false
      );
    else return true;
  }

  public getNewSpaceNonAddedTagList() {
    if (!this.createFilterTagMenu) {
      return this.spacesStore
        .userTags()
        .filter((tag) => !this.createSpaceTagList?.some((spaceTag) => spaceTag.uuid === tag.uuid));
    }
    return this.spacesStore
      .userTags()
      .filter((tag) => !this.createSpaceTagList?.some((spaceTag) => spaceTag.uuid === tag.uuid))
      .filter((tag) => tag.name.toLowerCase().includes(this.createFilterTagMenu.toLowerCase()));
  }

  public addTagToNewSpaceList(tag: SpaceTag) {
    this.createSpaceTagList.push(tag);
  }

  public deleteTagFromNewSpaceList(tagId: string) {
    this.createSpaceTagList = this.createSpaceTagList.filter((tag) => tag.uuid != tagId);
  }

  public getShowAddButton() {
    return !this.spacesStore
      .userTags()
      .some((tag) => tag.name.toLowerCase() === this.createFilterTagMenu.toLowerCase());
  }

  onFilterInputResize(event: Event): void {
    const inputEl = event.target as HTMLInputElement | null;
    if (!inputEl) return;
    const min = 50;
    const max = 200;
    const computed = Math.min(Math.max(min, inputEl.value.length * 10), max);
    inputEl.style.width = `${computed}px`;
  }

  onTagsDropdownOpen(isOpen: boolean) {
    if (isOpen) {
      setTimeout(() => {
        const input = this.filterInput?.nativeElement;
        if (input) {
          input.focus();
          input.select();
        }
      });
    }
  }

  onSpaceTagsFilterMenuInput(event: Event): void {
    const inputEl = event.target as HTMLInputElement | null;
    if (!inputEl) return;
    this.createFilterTagMenuSignal.set(inputEl.value);
  }

  onSpaceCardTagsDropdownChange(isOpen: boolean): void {
    if (isOpen) {
      setTimeout(() => {
        this.isSomeTagMenuOpen = true;
        const input = this.filterInput?.nativeElement;
        if (input) {
          input.focus();
          input.select();
        }
      }, 100);
    } else this.isSomeTagMenuOpen = false;
  }

  isTextTruncated(element: HTMLElement): boolean {
    return element.scrollWidth > element.clientWidth;
  }

  getTooltipText(tagName: string, element: HTMLElement): string | null {
    return this.isTextTruncated(element) ? tagName : null;
  }

  checkTagInputLength(event: KeyboardEvent): void {
    const inputEl = event.target as HTMLInputElement | null;
    if (!inputEl) return;
    if (this.errorText && this.errorText.length <= this.maxTagInputLength) {
      this.errorText = '';
    }
    if (
      inputEl.value.length >= this.maxTagInputLength &&
      event.key !== 'Backspace' &&
      event.key !== 'Delete' &&
      !event.ctrlKey
    ) {
      getTranslation(this.translateService, 'space_tag_max_length_error').subscribe((t: any) => {
        this.errorText = t?.replace ? t.replace('{{maxLength}}', String(this.maxTagInputLength)) : t;
      });
      event.preventDefault();
    }
  }
}
